import { ref } from 'vue'
import axios, { type AxiosError } from 'axios'
import { useGlobalStore } from '@/stores/global'

export interface LoginErrors {
  email?: string[]
  password?: string[]
  non_field_errors?: string[]
}

export interface LoginResponse {
  success?: boolean
  email_verification_required?: boolean
  message?: string
}

const FALLBACK_LOGIN_ERROR = 'Something went wrong. Please try again.'

export function useAuth() {
  const store = useGlobalStore()

  const errors = ref<LoginErrors | null>(null)
  const inFlight = ref(false)
  const emailVerificationRequired = ref(false)

  async function login(email: string, password: string, redirectUrl?: string) {
    inFlight.value = true
    errors.value = null
    emailVerificationRequired.value = false

    try {
      const response = await axios.post<LoginResponse>(
        '/api/login/',
        {
          username: email,
          password: password,
        },
        {
          headers: {
            'X-CSRFToken': store.csrfToken,
          },
        },
      )

      if (response.data.success) {
        window.location.href = redirectUrl || '/dashboard/'
      }
    } catch (e) {
      const err = e as AxiosError<LoginResponse>

      if (
        err.response?.status === 403 &&
        err.response?.data?.email_verification_required
      ) {
        emailVerificationRequired.value = true
        errors.value = {
          non_field_errors: [
            err.response.data.message ||
              'Please verify your email address before logging in.',
          ],
        }
      } else if (err.response?.data) {
        const data = err.response.data as LoginErrors & { detail?: string }
        if (data.non_field_errors || data.email || data.password) {
          errors.value = data
        } else {
          // DRF auth failures arrive as {detail: "..."}, which no form field renders.
          errors.value = {
            non_field_errors: [data.detail || FALLBACK_LOGIN_ERROR],
          }
        }
      } else {
        errors.value = {
          non_field_errors: [FALLBACK_LOGIN_ERROR],
        }
      }
      inFlight.value = false
    }
  }

  // Navigating to /accounts/logout/ used to work, but Django's LogoutView is POST-only
  // as of 5.0 and would now 405.
  async function logout(redirectUrl?: string) {
    try {
      await axios.post(
        '/api/logout/',
        {},
        {
          headers: {
            'X-CSRFToken': store.csrfToken,
          },
        },
      )
    } catch {
      // Swallowed on purpose: if the session is already gone the user still wants out,
      // and there is nothing they could do about a failure here anyway.
    }

    window.location.href = redirectUrl || '/'
  }

  return { errors, inFlight, emailVerificationRequired, login, logout }
}
